"use client";

import { c as _c } from "react/compiler-runtime";
import { Lock } from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAccessDeniedStore } from "@/stores/useAccessDeniedStore";
export interface AccessDeniedProps {
  message: string;
  backHref: string;
  backLabel?: string;
  className?: string;
}

/**
 * Full-page view when the user does not have permission to view a resource (403 view-forbidden).
 * Unboxed: content is centered in the main area with no card/border so it feels full-page.
 * Use with access-denied store; message typically comes from getErrorMessage(problemDetail).
 * Uses theme tokens so it aligns with app look and supports light and dark mode.
 */
export function AccessDenied(t0) {
  const $ = _c(16);
  const {
    message,
    backHref,
    backLabel: t1,
    className
  } = t0;
  const backLabel = t1 === undefined ? "Back to dashboard" : t1;
  const clearAccessDenied = useAccessDeniedStore(_temp);
  let t2;
  if ($[0] !== className) {
    t2 = cn("flex flex-1 min-h-0 w-full flex-col items-center justify-center gap-5 px-4 text-center", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Lock className="h-14 w-14 shrink-0 text-muted-foreground" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <h1 className="text-2xl font-semibold text-foreground">You don't have access</h1>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== message) {
    t5 = <div className="space-y-2">{t4}<p className="text-muted-foreground max-w-lg">{message}</p></div>;
    $[4] = message;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== clearAccessDenied) {
    t6 = () => clearAccessDenied();
    $[6] = clearAccessDenied;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== backHref || $[9] !== backLabel || $[10] !== t6) {
    t7 = <Button asChild={true} variant="default" size="sm"><Link href={backHref} onClick={t6}>{backLabel}</Link></Button>;
    $[8] = backHref;
    $[9] = backLabel;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t2 || $[13] !== t5 || $[14] !== t7) {
    t8 = <div className={t2} role="alert" aria-live="assertive">{t3}{t5}{t7}</div>;
    $[12] = t2;
    $[13] = t5;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}
function _temp(s) {
  return s.clearAccessDenied;
}